import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { API_URLS } from '../../../config/api';
import { Plus, Edit, Trash2, Search, Tag, Percent, DollarSign, Eye, EyeOff, Sparkles, RefreshCw } from 'lucide-react';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [wheelEnabled, setWheelEnabled] = useState(true);

  // Form states
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    discount_type: 'percentage',
    discount_value: '',
    is_active: true
  });

  useEffect(() => {
    fetchCoupons();
    fetchWheelSettings();
  }, []);

  const fetchWheelSettings = async () => {
    try {
      const response = await fetch(API_URLS.ADMIN.GET_COUPON_WHEEL_SETTINGS);
      if (response.ok) {
        const data = await response.json();
        // Response format: {status: 'success', is_enabled: 1} veya {status: 'error', is_enabled: 1}
        const enabled = data.is_enabled === 1 || data.is_enabled === true || data.is_enabled === '1';
        setWheelEnabled(enabled);
      } else {
        // Hata durumunda varsayılan olarak aktif
        setWheelEnabled(true);
      }
    } catch (error) {
      console.error('Error fetching wheel settings:', error);
      // Hata durumunda varsayılan olarak aktif
      setWheelEnabled(true);
    }
  };

  const handleToggleWheel = async (enabled) => {
    try {
      const response = await fetch(API_URLS.ADMIN.UPDATE_COUPON_WHEEL_SETTINGS, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_enabled: enabled })
      });
      if (response.ok) {
        setWheelEnabled(enabled);
      }
    } catch (error) {
      console.error('Error updating wheel settings:', error);
    }
  };

  const handleResetWheelCache = () => {
    if (window.confirm('Çarkıfelek cache\'ini sıfırlamak istediğinizden emin misiniz? Bu işlem sadece sizin tarayıcınızdaki cache\'i sıfırlar.')) {
      localStorage.removeItem('coupon_wheel_spun');
      // Popup'ı manuel olarak açmak için event dispatch et
      window.dispatchEvent(new CustomEvent('openCouponWheel'));
      alert('Çarkıfelek cache\'i başarıyla sıfırlandı! Çarkıfelek popup\'ı açılıyor...');
    }
  };

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URLS.ADMIN.COUPONS);
      if (response.ok) {
        const data = await response.json();
        // is_active değerlerini normalize et (string "0"/"1" veya integer 0/1 olabilir)
        const normalizedCoupons = (data.coupons || []).map(coupon => ({
          ...coupon,
          is_active: coupon.is_active === 1 || coupon.is_active === true || coupon.is_active === '1'
        }));
        setCoupons(normalizedCoupons);
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingCoupon 
        ? API_URLS.ADMIN.UPDATE_COUPON
        : API_URLS.ADMIN.CREATE_COUPON;
      
      const method = editingCoupon ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          discount_value: parseFloat(formData.discount_value),
          is_active: formData.is_active ? 1 : 0, // Boolean'ı integer'a çevir
          id: editingCoupon?.id
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          await fetchCoupons();
          resetForm();
          setShowModal(false);
        } else {
          alert('Hata: ' + (result.message || 'Kupon kaydedilemedi'));
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert('Hata: ' + (errorData.message || 'Kupon kaydedilemedi'));
      }
    } catch (error) {
      console.error('Error saving coupon:', error);
      alert('Hata: Kupon kaydedilemedi. ' + error.message);
    }
  };

  const handleDelete = async (couponId) => {
    if (window.confirm('Bu kuponu silmek istediğinizden emin misiniz?')) {
      try {
        const response = await fetch(API_URLS.ADMIN.DELETE_COUPON(couponId));
        if (response.ok) {
          await fetchCoupons();
        }
      } catch (error) {
        console.error('Error deleting coupon:', error);
      }
    }
  };

  const handleToggleStatus = async (couponId) => {
    try {
      const response = await fetch(API_URLS.ADMIN.TOGGLE_COUPON_STATUS(couponId), {
        method: 'GET'
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          await fetchCoupons();
        } else {
          alert('Hata: ' + (result.message || 'Kupon durumu değiştirilemedi'));
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert('Hata: ' + (errorData.message || 'Kupon durumu değiştirilemedi'));
      }
    } catch (error) {
      console.error('Error toggling coupon status:', error);
      alert('Hata: Kupon durumu değiştirilemedi. ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      title: '',
      discount_type: 'percentage',
      discount_value: '',
      is_active: true
    });
    setEditingCoupon(null);
  };

  const openModal = (coupon = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setFormData({
        code: coupon.code,
        title: coupon.title,
        discount_type: coupon.discount_type,
        discount_value: coupon.discount_value,
        is_active: coupon.is_active === 1 || coupon.is_active === true
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const filteredCoupons = coupons.filter(coupon =>
    coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coupon.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">İndirim Kuponları</h1>
          <p className="text-sm text-gray-600 mt-1">Kupon kodlarını yönetin</p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
          {/* Çarkıfelek Açma/Kapama ve Cache Sıfırlama */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <div className="flex items-center gap-2">
                <Switch
                  id="wheel-enabled"
                  checked={wheelEnabled}
                  onCheckedChange={handleToggleWheel}
                />
                <Label htmlFor="wheel-enabled" className="text-sm font-medium cursor-pointer">
                  Çarkıfelek {wheelEnabled ? 'Açık' : 'Kapalı'}
                </Label>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetWheelCache}
              className="text-xs"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Cache Sıfırla
            </Button>
          </div>
          <Button onClick={() => openModal()} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Kupon
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <Input
          type="text"
          placeholder="Kupon kodu veya başlık ile ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Coupons List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Yükleniyor...</p>
        </div>
      ) : filteredCoupons.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Henüz kupon eklenmemiş</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCoupons.map((coupon) => (
            <Card key={coupon.id} className={`${coupon.is_active === 1 || coupon.is_active === true ? '' : 'opacity-60'}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-semibold text-gray-900 mb-1">
                      {coupon.title}
                    </CardTitle>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="font-mono text-sm">
                        {coupon.code}
                      </Badge>
                      {coupon.is_active === 1 || coupon.is_active === true ? (
                        <Badge variant="default" className="bg-green-500">
                          Aktif
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Pasif</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 mb-4">
                  {coupon.discount_type === 'percentage' ? (
                    <>
                      <Percent className="w-5 h-5 text-purple-600" />
                      <span className="text-2xl font-bold text-purple-600">
                        %{parseFloat(coupon.discount_value).toFixed(0)}
                      </span>
                      <span className="text-gray-600">İndirim</span>
                    </>
                  ) : (
                    <>
                      <DollarSign className="w-5 h-5 text-purple-600" />
                      <span className="text-2xl font-bold text-purple-600">
                        {parseFloat(coupon.discount_value).toFixed(2)} ₺
                      </span>
                      <span className="text-gray-600">İndirim</span>
                    </>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openModal(coupon)}
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Düzenle
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleStatus(coupon.id)}
                    className="flex-1"
                  >
                    {coupon.is_active === 1 || coupon.is_active === true ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-1" />
                        Pasif Yap
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        Aktif Yap
                      </>
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(coupon.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {editingCoupon ? 'Kupon Düzenle' : 'Yeni Kupon Ekle'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kupon Kodu *
                  </label>
                  <Input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="ÖRN: YAZ2024"
                    required
                    className="font-mono"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kupon Başlığı *
                  </label>
                  <Input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="ÖRN: Yaz İndirimi"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    İndirim Tipi *
                  </label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="percentage">Yüzde (%)</option>
                    <option value="fixed">Sabit Tutar (₺)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    İndirim Değeri *
                  </label>
                  <div className="relative">
                    {formData.discount_type === 'percentage' ? (
                      <>
                        <Input
                          type="number"
                          value={formData.discount_value}
                          onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                          placeholder="ÖRN: 20"
                          min="0"
                          max={formData.discount_type === 'percentage' ? '100' : ''}
                          step="0.01"
                          required
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                      </>
                    ) : (
                      <>
                        <Input
                          type="number"
                          value={formData.discount_value}
                          onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                          placeholder="ÖRN: 50"
                          min="0"
                          step="0.01"
                          required
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">₺</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">
                    Aktif
                  </label>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1"
                  >
                    İptal
                  </Button>
                  <Button type="submit" className="flex-1">
                    {editingCoupon ? 'Güncelle' : 'Oluştur'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;

